import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useFinalizeRound } from "../../api/invoices.js";
import { useSupplierDashboard } from "../../api/supplier.js";
import { AppShell } from "../../components/AppShell.js";
import { buttonClass, inputClass } from "../../components/Field.js";
import { formatMoney, formatQty } from "../../lib/format.js";

export function SupplierFinalizePage() {
  const { roundId = "" } = useParams();
  const navigate = useNavigate();
  const { data, isPending } = useSupplierDashboard();
  const finalize = useFinalizeRound();
  const round = data?.rounds.find((r) => r.id === roundId);
  const pending = round?.participants.filter((p) => p.status === "confirmed") ?? [];

  // Default is the ordered quantity — correct most of the time; only meat needs a manual fix.
  const [actual, setActual] = useState<Record<string, string>>({});
  const valueFor = (commitmentId: string, orderedQty: string) => actual[commitmentId] ?? orderedQty;

  if (isPending) {
    return (
      <AppShell title="납품 확정">
        <p className="text-stone-500">불러오는 중…</p>
      </AppShell>
    );
  }

  if (!round) {
    return (
      <AppShell title="납품 확정">
        <p className="text-red-600">라운드를 찾을 수 없습니다.</p>
      </AppShell>
    );
  }

  if (round.status !== "confirmed") {
    return (
      <AppShell title="납품 확정">
        <p className="text-red-600">확정된 라운드만 납품 확정할 수 있습니다.</p>
      </AppShell>
    );
  }

  return (
    <AppShell title="납품 확정">
      <p className="mb-2 font-semibold">
        {round.sku.nameKo} · {formatMoney(round.dealUnitPrice)} / {round.sku.unit}
      </p>
      <p className="mb-4 text-sm text-stone-600">
        실제로 납품한 수량을 입력해 주세요. 청구서는 이 수량으로 발행됩니다. 공급하지 못한 곳은 0을
        입력하면 청구서가 만들어지지 않습니다.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          finalize.mutate(
            {
              id: round.id,
              lines: pending.map((p) => ({
                commitmentId: p.commitmentId,
                fulfilledQty: valueFor(p.commitmentId, p.qty),
              })),
            },
            { onSuccess: () => navigate("/supplier/invoices") },
          );
        }}
      >
        <ul className="mb-4">
          {pending.map((p) => {
            const value = valueFor(p.commitmentId, p.qty);
            const noDelivery = Number(value) === 0;
            return (
              <li key={p.commitmentId} className="border-b border-stone-200 py-3">
                <div className="mb-1 flex justify-between">
                  <span className="font-medium">{p.restaurantName}</span>
                  <span className="text-sm text-stone-500">주문 {formatQty(p.qty, round.sku)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    aria-label={`${p.restaurantName} 실납품 수량`}
                    inputMode="decimal"
                    className={inputClass}
                    value={value}
                    onChange={(e) => setActual({ ...actual, [p.commitmentId]: e.target.value })}
                  />
                  <span>{round.sku.unit}</span>
                </div>
                {noDelivery ? (
                  <p className="mt-1 text-sm text-amber-700">0 입력 시 이 식당에는 청구서가 발행되지 않습니다.</p>
                ) : null}
              </li>
            );
          })}
        </ul>

        {pending.length === 0 ? <p className="mb-4 text-stone-500">최종 확인한 식당이 없습니다.</p> : null}

        {finalize.error ? (
          <p role="alert" className="mb-3 text-sm text-red-600">
            {(finalize.error as Error).message}
          </p>
        ) : null}
        <button type="submit" className={buttonClass} disabled={finalize.isPending || pending.length === 0}>
          {finalize.isPending ? "발행 중…" : "납품 확정하고 청구서 발행"}
        </button>
      </form>
    </AppShell>
  );
}
